/**
 * StoryboardDesignerNode (Canvas V2)
 *
 * Displays the 3x3 storyboard grid image. Visual layout matches
 * ImageGeneratorNode — same card styling, quick-info bar, toolbar,
 * selection behavior, loading states, and UnifiedBottomPanel with
 * Config/Refine/Variants tabs.
 *
 * Width: 400px fixed. Aspect: 16:9 for the grid image area.
 */

import { memo, useState, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Handle, Position, type NodeProps, useEdges, useReactFlow } from 'reactflow'
import { BookmarkPlus, MessageSquarePlus } from 'lucide-react'
import { getThumbnailUrl, getAssetUrl, executeNode, cancelNodeExecution, InsufficientSparksError, createDebug, type StoryboardDesignerNodeData } from '@ponpon/api-client'
import { useClickLock } from '../../../../hooks/useClickLock'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useCanvasOptional } from '../../CanvasContext'
import type { ReactFlowCanvasEdgeData, ReactFlowNodeData } from '../../../../hooks/useYCanvas'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  UnifiedBottomPanel,
  STORYBOARD_TABS,
    DEFAULT_TAB,
  ConfigTabContent,
  RefineTabContent,
  VariantsTabContent,
  useNodeActions,
  useBottomPanelAutoOpen,
  type ToolbarAction,
  type SelectedReference,
  buildAssetReferencesFromEdges,
  buildImageReferenceEdgeUpdates,
  getImageOutputPort,
  createHandlesFromSchema,
} from '../shared'
import type { HistoryEntry } from '../shared/types'
import { buildAssetDownloadFilename } from '../../../../utils/buildDownloadFilename'
import storyboardDesignerSchema from '../../../../../../../schemas/nodes/storyboardDesigner.json'

export type { StoryboardDesignerNodeData }

const debug = createDebug('dw:storyboard-video:node')

const NODE_WIDTH = 400

const HANDLES = createHandlesFromSchema(storyboardDesignerSchema, {
  inputDefaults: { position: Position.Top },
  outputDefaults: { position: Position.Bottom },
  inputs: {
    in_script: { position: Position.Left },
    in_image: { position: Position.Left, style: { top: '68%' } },
    in_characters: { style: { left: '30%' } },
    in_character_names: { style: { left: '40%' } },
    in_locations: { style: { left: '50%' } },
    in_location_names: { style: { left: '60%' } },
    in_objects: { style: { left: '70%' } },
    in_object_names: { style: { left: '80%' } },
  },
  outputs: {
    out_storyboard_grid: { position: Position.Right },
    out_beats: { style: { left: '40%' } },
    out_panel_refs: { style: { left: '60%' } },
    out_metadata: { position: Position.Right, style: { top: '65%' } },
  },
})

const StoryboardDesignerNode = memo(function StoryboardDesignerNode(
  props: NodeProps<StoryboardDesignerNodeData>
) {
  const { id, data, selected, dragging } = props
  const {
    cacheKey,
    out_storyboard_grid,
    in_script = '',
    in_scene_id = 'scene_1',
    in_style,
    in_model = 'openai-gpt-image-2',
    status = 'idle',
    progressMessage,
    error,
  } = data
  const projectId = useProjectId()
  const { setNodes, getNodes, getEdges } = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()
  const edges = useEdges<ReactFlowCanvasEdgeData>()
  const canvas = useCanvasOptional()
  const { handleDelete, handleDuplicate, handleDownload, handleSaveToLibrary, handleAddToChat } = useNodeActions({ nodeId: id })

  const [isModalOpen, setIsModalOpen] = useState(false)

  // ── Grid images (variants) ────────────────────────────────────────────

  const gridImages = useMemo(() => {
    if (!out_storyboard_grid) return []
    return out_storyboard_grid
  }, [out_storyboard_grid])

  const pickedIndex = data.picked_storyboard_index ?? (gridImages.length > 0 ? gridImages.length - 1 : 0)
  const batchPending = (status === 'running') ? (data.batch_pending ?? 0) : 0

  const gridImagePath = useMemo(() => {
    if (gridImages.length === 0) return undefined
    if (pickedIndex >= 0 && pickedIndex < gridImages.length) {
      return gridImages[pickedIndex]
    }
    return gridImages[gridImages.length - 1]
  }, [gridImages, pickedIndex])

  const imageUrl = gridImagePath && projectId
    ? getThumbnailUrl(projectId, gridImagePath, 0, cacheKey)
    : null
  const fullImageUrl = gridImagePath && projectId
    ? getAssetUrl(projectId, gridImagePath, 0)
    : null

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isRunning || isPendingExecution || isPendingCost
  const spinClass = isRunning ? 'animate-spin' : isPendingCost ? '' : 'animate-spin-slow'
  const isError = status === 'error'
  const variantCount = gridImages.length
  const [isCancelling, setIsCancelling] = useState(false)
  const handleCancel = useCallback(() => {
    if (!isRunning || !projectId || isCancelling) return
    setIsCancelling(true)
    cancelNodeExecution(projectId, id)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('cancelNodeExecution failed:', err)
      })
      .finally(() => setIsCancelling(false))
  }, [isRunning, projectId, id, isCancelling])

  debug('render node=%s status=%s gridPath=%s variants=%d', id, status, gridImagePath, variantCount)

  // ── History entries for VariantsTabContent ─────────────────────────────

  const historyEntries = useMemo<HistoryEntry[]>(
    () => gridImages.map((path, i) => ({
      result: { id: `grid_v${i + 1}`, local_path: path },
      user_prompt: '',
      refs: [],
      metadata: {},
    })),
    [gridImages]
  )

  const handleHistorySelect = useCallback(
    (index: number) => {
      const entry = historyEntries[index]
      const localPath = entry?.result?.local_path
      if (!localPath) return

      const newCacheKey = String(Date.now())
      setNodes((nds) =>
        nds.map((n) =>
          n.id !== id
            ? n
            : {
                ...n,
                data: {
                  ...n.data,
                  picked_storyboard_index: index,
                  cacheKey: newCacheKey,
                },
              }
        )
      )
      canvas?.updateNodeData(id, {
        picked_storyboard_index: index,
        cacheKey: newCacheKey,
      })
    },
    [id, historyEntries, setNodes, canvas]
  )

  // ── Bottom panel state ────────────────────────────────────────────────

  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)

  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      setNodes((nodes) => nodes.map((n) => ({ ...n, selected: n.id === id })))
    },
    [id, setNodes]
  )

  // ── Config updates ─────────────────────────────────────────────────────

  const updateConfig = useCallback(
    (key: string, value: string | string[] | undefined) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [key]: value } } : n))
      )
      canvas?.updateNodeData(id, { [key]: value })
    },
    [id, setNodes, canvas]
  )

  // ── Generate handler ───────────────────────────────────────────────────

  const showInsufficientSparks = useShowInsufficientSparks()
  const { isLocked, run } = useClickLock(1000)
  const generateLocked = isLocked(id)

  const handleGenerate = useCallback(async (retryCount: number = 1) => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation') return
    if (!projectId) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: 'Missing projectId.' } }
            : n
        )
      )
      return
    }

    void run(id, async () => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'running', progress: 0, error: undefined } }
            : n
        )
      )

      try {
        await executeNode(projectId, id, undefined, retryCount)
      } catch (err) {
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({ balance: err.balance, required: err.required, nodeId: id })
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id
                ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
                : n
            )
          )
          return
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n
          )
        )
      }
    })
  }, [projectId, id, status, setNodes, run, showInsufficientSparks])

  // ── Refinement handler ────────────────────────────────────────────────

  const [feedbackText, setFeedbackText] = useState('')

  const handleRefineSubmit = useCallback(async () => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation' || !projectId) return

    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                status: 'running',
                progressMessage: 'Refining storyboard...',
                error: undefined,
              },
            }
          : n
      )
    )

    try {
      if (feedbackText.trim()) {
        canvas?.updateNodeData(id, { in_feedback: feedbackText.trim() })
      }
      await executeNode(projectId, id)
      setFeedbackText('')
    } catch (err) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
            : n
        )
      )
    }
  }, [projectId, id, feedbackText, status, setNodes, canvas])

  // ── Reference handling for ConfigTabContent ───────────────────────────

  const currentRefs = useMemo<SelectedReference[]>(() => {
    const nodes = getNodes()
    return [
      ...buildAssetReferencesFromEdges(nodes, edges, id, 'in_characters', 'image'),
      ...buildAssetReferencesFromEdges(nodes, edges, id, 'in_locations', 'image'),
      ...buildAssetReferencesFromEdges(nodes, edges, id, 'in_objects', 'image'),
      ...buildAssetReferencesFromEdges(nodes, edges, id, 'in_image', 'image').slice(0, 1),
    ]
  }, [getNodes, edges, id])
  const selectedReferenceImage = currentRefs[0]?.assetPath

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      if (canvas) {
        const characterRefs = refs.filter((ref) => ref.nodeType === 'characterDesigner')
        const locationRefs = refs.filter((ref) => ref.nodeType === 'locationDesigner')
        const objectRefs = refs.filter((ref) => ref.nodeType === 'objectDesigner')
        const genericRefs = refs.filter(
          (ref) => !['characterDesigner', 'locationDesigner', 'objectDesigner'].includes(ref.nodeType)
        )
        const nameEdges = (items: SelectedReference[]) =>
          items.map((ref, index) => ({
            source: ref.nodeId,
            sourceHandle: 'out_name',
            toIndex: index,
          }))
        const imageEdges = (items: SelectedReference[]) =>
          items.map((ref, index) => ({
            source: ref.nodeId,
            sourceHandle: getImageOutputPort(ref.nodeType),
            toIndex: index,
          }))

        const applyEdges = () => {
          canvas.replacePortEdges(id, 'in_characters', imageEdges(characterRefs))
          canvas.replacePortEdges(id, 'in_character_names', nameEdges(characterRefs))
          canvas.replacePortEdges(id, 'in_locations', imageEdges(locationRefs))
          canvas.replacePortEdges(id, 'in_location_names', nameEdges(locationRefs))
          canvas.replacePortEdges(id, 'in_objects', imageEdges(objectRefs))
          canvas.replacePortEdges(id, 'in_object_names', nameEdges(objectRefs))
          canvas.replacePortEdges(id, 'in_image', buildImageReferenceEdgeUpdates(genericRefs.slice(0, 1)))
        }

        if (canvas.transact) {
          canvas.transact(applyEdges)
        } else {
          applyEdges()
        }
      }
    },
    [canvas, id]
  )

  // ── Download handler ───────────────────────────────────────────────────

  const onDownload = useCallback(() => {
    if (!gridImagePath) return
    const { filename } = buildAssetDownloadFilename({
      canvasNodes: getNodes(),
      canvasEdges: getEdges(),
      nodeId: id,
      assetPath: gridImagePath,
      mediaType: 'image',
    })
    handleDownload(gridImagePath, filename)
  }, [gridImagePath, handleDownload, getNodes, getEdges, id])

  // ── Toolbar actions (same pattern as ImageGeneratorNode) ──────────────

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'expand',
        label: 'Expand',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4" />
          </svg>
        ),
        onClick: () => setIsModalOpen(true),
        hidden: !imageUrl,
        group: true,
      },
      {
        key: 'download',
        label: 'Download',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        ),
        onClick: onDownload,
        hidden: !imageUrl,
      },
      {
        key: 'save-to-library',
        label: 'Save to Library',
        icon: <BookmarkPlus className="w-4 h-4" />,
        onClick: handleSaveToLibrary,
        group: true,
      },
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [imageUrl, onDownload, handleDuplicate, handleDelete, handleSaveToLibrary, handleAddToChat]
  )

  // ── Panel visibility ──────────────────────────────────────────────────

  const panelVisible = !!selected && !dragging && bottomPanelOpen 

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <div className="relative">
        <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />
        <FloatingNodeLabel name={in_scene_id || ''} fallback="Storyboard" />

        <div
          data-testid="storyboard-designer-node"
          className={`rounded-lg overflow-hidden transition-all duration-150 ${showSpinner ? 'border-2 animate-border-glow' : ''}`}
          style={{
            width: NODE_WIDTH,
            boxShadow: selected
              ? '0 0 0 2px rgba(255,255,255,0.7)'
              : isError
                ? '0 0 0 1px rgba(239,68,68,0.5)'
                : showSpinner
                  ? '0 0 0 1px rgba(234,179,8,0.5)'
                  : '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          {/* Handles */}
          {HANDLES.map((handle) => (
            <Handle
              key={handle.id}
              type={handle.type}
              position={handle.position}
              id={handle.id}
              className="!bg-transparent !border-0 !w-2 !h-2"
              style={handle.style}
            />
          ))}

          {/* Main content area */}
          <div className="rounded-lg overflow-hidden">
            {showSpinner ? (
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center px-4"
                style={{ aspectRatio: '16/9' }}
              >
                <div className={`w-6 h-6 border-2 border-[var(--warning)] border-t-transparent rounded-full ${spinClass}`} />
                <span className="text-xxs text-foreground/50 mt-2 text-center leading-relaxed">
                  {isRunning
                    ? (progressMessage || 'Generating storyboard grid...')
                    : isPendingCost
                      ? 'Waiting for cost approval in chat...'
                      : 'Queued — waiting for upstream nodes...'}
                </span>
                {isRunning && (
                  <span className="text-xxs text-foreground/25 mt-1">
                    This may take up to 60 seconds
                  </span>
                )}
                {isRunning && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCancel() }}
                    disabled={isCancelling}
                    className="mt-3 flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors nodrag"
                    data-testid="cancel-node-button"
                    title="Stop"
                  >
                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </svg>
                    <span className="text-xxs font-medium">Stop</span>
                  </button>
                )}
              </div>
            ) : imageUrl ? (
              <div
                className="relative group"
                style={{ aspectRatio: '16/9', overflow: 'hidden' }}
              >
                <img
                  src={imageUrl}
                  alt="Storyboard Grid"
                  className={`w-full h-full object-cover block transition-transform duration-300 ease-out ${
                    !selected ? 'group-hover:scale-[1.02]' : ''
                  }`}
                />
                {/* Overlay controls */}
                <div className="absolute top-2 right-2 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsModalOpen(true) }}
                    className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                    title="Expand"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDownload() }}
                    className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                    title="Download"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                </div>
              </div>
            ) : isError && error ? (
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center px-6"
                style={{ aspectRatio: '16/9' }}
              >
                <svg className="w-8 h-8 text-red-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <span className="text-xxs text-red-400/80 mt-1.5 text-center line-clamp-2 cursor-help" title={error}>
                  {sanitizeNodeError(error)}
                </span>
                <span className="text-xxs text-foreground/20 mt-1">Click to retry</span>
              </div>
            ) : (
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center"
                style={{ aspectRatio: '16/9' }}
              >
                <svg className="w-8 h-8 text-foreground/10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                </svg>
                <span className="text-xxs text-foreground/20 mt-1">Storyboard</span>
              </div>
            )}
          </div>

          {/* Quick Info Bar — 3 columns: Config | Variants | Reference */}
          {!showSpinner && (
            <div
              data-testid="quick-info-bar"
              className="grid grid-cols-3 border-t border-border-subtle bg-surface-0"
            >
              {/* Config Column */}
              <button
                data-testid="quick-info-config"
                onClick={(e) => {
                  e.stopPropagation()
                  openPanelToTab('config')
                }}
                className="py-2.5 px-3 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle overflow-hidden min-w-0"
              >
                <span className="text-[10px] text-muted-foreground leading-none">Config</span>
              </button>

              {/* Variants Column */}
              <button
                data-testid="quick-info-variants"
                onClick={(e) => {
                  e.stopPropagation()
                  openPanelToTab('variants')
                }}
                className="py-2.5 px-3 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle"
              >
                <span className="text-[10px] text-muted-foreground leading-none">Variants</span>
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: variantCount > 0 ? 'var(--primary)' : 'var(--text-muted)',
                    }}
                  />
                  <span className="text-xs font-medium text-foreground">
                    {variantCount}
                  </span>
                </div>
              </button>

              {/* Reference Column */}
              <button
                data-testid="quick-info-reference"
                onClick={(e) => {
                  e.stopPropagation()
                  openPanelToTab('config')
                }}
                className="py-2.5 px-3 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag"
              >
                <span className="text-[10px] text-muted-foreground leading-none">Reference</span>
                <div className="flex items-center gap-1">
                  {selectedReferenceImage ? (
                    (() => {
                      const thumbUrl = projectId
                        ? getThumbnailUrl(projectId, selectedReferenceImage, 32, cacheKey)
                        : null
                      return thumbUrl ? (
                        <div className="w-6 h-6 rounded overflow-hidden border border-border">
                          <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : null
                    })()
                  ) : (
                    <span className="text-xs text-text-muted/40">--</span>
                  )}
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Unified Bottom Panel */}
        <UnifiedBottomPanel
          tabs={STORYBOARD_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isVisible={panelVisible}
        >
          {(tab) => {
            if (tab === 'config' && projectId) {
              return (
                <ConfigTabContent
                  nodeId={id}
                  nodeType="storyboardDesigner"
                  prompt={in_script}
                  onPromptChange={(value) => updateConfig('in_script', value)}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  currentStyleId={in_style}
                  onStyleChange={(styleId) => updateConfig('in_style', styleId)}
                  model={in_model}
                  onModelChange={(model) => updateConfig('in_model', model)}
                  isRunning={isRunning || generateLocked}
                  onGenerate={handleGenerate}
                  onCancel={handleCancel}
                  isCancelling={isCancelling}
                />
              )
            }
            if (tab === 'refine') {
              return (
                <RefineTabContent
                  feedbackText={feedbackText}
                  onFeedbackTextChange={setFeedbackText}
                  onSubmit={handleRefineSubmit}
                  isRunning={isRunning}
                  nodeType="storyboardDesigner"
                  selectedStyleId={in_style}
                  onStyleChange={(styleId) => updateConfig('in_style', styleId)}
                />
              )
            }
            if (tab === 'variants') {
              return (
                <VariantsTabContent
                  history={historyEntries}
                  pickedIndex={pickedIndex}
                  onSelectVariant={handleHistorySelect}
                  batchPending={batchPending}
                  cacheKey={cacheKey}
                  projectId={projectId}
                  accentColor="purple"
                  aspectRatio="16/9"
                />
              )
            }
            return null
          }}
        </UnifiedBottomPanel>
      </div>

      {/* Fullscreen lightbox */}
      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={fullImageUrl ?? undefined}
                alt="Full size"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

StoryboardDesignerNode.displayName = 'StoryboardDesignerNode'
export default StoryboardDesignerNode
